import isPlainObject from 'lodash/isPlainObject';
import transform from 'lodash/transform';
import isArray from 'lodash/isArray';
import snakeCase from 'lodash/snakeCase';
import ObjectUtils from '@spothero/utils/object';

/**
 * Convert camel case keys to snake case keys
 */
export type CamelToSnake<S extends string> = S extends `${infer T}${infer U}`
    ? U extends Uncapitalize<U>
        ? `${Uncapitalize<T>}${CamelToSnake<U>}`
        : `${Uncapitalize<T>}_${CamelToSnake<Uncapitalize<U>>}`
    : S;

/**
 * Convert snake case keys to camel case keys
 */
export type CamelizedKeys<T> = {
    [K in keyof T as K extends string
        ? CamelToSnake<Uncapitalize<K>>
        : K]: T[K] extends Array<infer U>
        ? Array<CamelizedKeys<U>> // Handle arrays
        : // eslint-disable-next-line @typescript-eslint/no-explicit-any
          T[K] extends Record<string, any>
          ? CamelizedKeys<T[K]> // Recurse into objects
          : T[K];
};

/**
 * Recursively converts the keys of an object to snake case.
 * @param obj - The object to convert.
 * @returns The object with all keys converted to snake case.
 */
export const convertKeysToSnakeCase = <T, O = T>(obj: T): O => {
    if (isPlainObject(obj)) {
        return transform(
            obj as Record<string, unknown>,
            (result: Record<string, unknown>, value: unknown, key: string) => {
                result[snakeCase(key)] = convertKeysToSnakeCase(value);
            },
            {} as Record<string, unknown>
        ) as unknown as O;
    } else if (isArray(obj)) {
        return (obj as unknown[]).map(convertKeysToSnakeCase) as O;
    } else {
        return obj as unknown as O;
    }
};
/**
 * Flattens the values of an object into an array, while ensuring that input types are correctly handled.
 * @param obj - The object or value to flatten.
 * @returns The flattened array of values.
 * @example
 * const obj = { a: 1, b: [2, 3], c: { d: 4, e: 5 } };
 * flattenObjectValues(obj); // Returns [1, 2, 3, 4, 5]
 */
export const flattenObjectValues = (obj: unknown): unknown[] => {
    if (
        typeof obj === 'string' ||
        typeof obj === 'number' ||
        typeof obj === 'boolean' ||
        typeof obj === 'symbol' ||
        typeof obj === 'function' ||
        isArray(obj)
    ) {
        console.error(
            `flattenObjectValues error: parameter must be a plain object with values to flatten. Parameter type: ${typeof obj}`
        );

        // If the input is not a plain object, return it wrapped in an array
        return [obj];
    }

    // If the input is a plain object, flatten its values
    if (!obj || Object.values(obj).length === 0) {
        return [];
    }

    return Object.entries(obj).reduce<unknown[]>(
        (acc, [, value]) =>
            Array.isArray(value)
                ? acc.concat(flattenObjectValues(value))
                : typeof value === 'object' && value !== null
                  ? acc.concat(flattenObjectValues(value))
                  : acc.concat(value),
        []
    );
};

/**
 * Converts the keys of an object to camel case.
 * @returns The object with all keys converted to camel case.
 */
export const camelizeKeys = <T, O = T>(data: T): O => {
    return ObjectUtils.camelizeKeys(data) as unknown as O;
};

/**
 * Checks if a string is in camelCase format
 * @param str The string to check
 * @returns True if the string is in camelCase, false otherwise
 */
const isCamelCase = (str: string): boolean => {
    if (str.length === 0) return false;
    if (!/^[a-z]/.test(str)) return false;
    if (/[ _-]/.test(str)) return false;
    if (!/^[a-zA-Z0-9]+$/.test(str)) return false;

    return true;
};

/**
 * Recursively checks if all keys in an object are camelCase
 * @param obj The object to check
 * @returns {boolean} True if all keys are camelCase, false otherwise
 */
export const areAllKeysCamelCase = (obj: unknown): boolean => {
    if (typeof obj !== 'object' || obj === null) {
        return true;
    }

    if (Array.isArray(obj)) {
        return obj.every((item) => areAllKeysCamelCase(item));
    }

    for (const key of Object.keys(obj)) {
        if (!isCamelCase(key)) {
            return false;
        }

        if (
            typeof obj[key as keyof typeof obj] === 'object' &&
            obj[key as keyof typeof obj] !== null
        ) {
            if (!areAllKeysCamelCase(obj[key as keyof typeof obj])) {
                return false;
            }
        }
    }

    return true;
};
